import React from 'react';
import { motion } from 'framer-motion';
import { ChatStats } from '../types';
import { BarChart3, MessageSquare, Users, Calendar, TrendingUp } from 'lucide-react';
import './StatsPanel.css';

interface StatsPanelProps {
  stats: ChatStats;
}

export const StatsPanel: React.FC<StatsPanelProps> = ({ stats }) => {
  const topSenders = stats.topSenders.slice(0, 5);

  return (
    <div className="stats-panel">
      <motion.div
        className="stats-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <BarChart3 size={24} />
        <h2>Chat Statistics</h2>
      </motion.div>

      <div className="stats-grid">
        <motion.div
          className="stat-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <div className="stat-icon">
            <MessageSquare size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalMessages.toLocaleString()}</div>
            <div className="stat-label">Total Messages</div>
          </div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
        >
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalChats}</div>
            <div className="stat-label">Total Chats</div>
          </div>
        </motion.div>

        <motion.div
          className="stat-card"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeYears.length}</div>
            <div className="stat-label">Years of Chats</div>
          </div>
        </motion.div>
      </div>

      {topSenders.length > 0 && (
        <motion.div
          className="top-senders"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3>
            <TrendingUp size={18} />
            <span>Top Senders</span>
          </h3>
          <div className="senders-list">
            {topSenders.map((sender, index) => {
              const maxCount = topSenders[0].count;
              const percentage = (sender.count / maxCount) * 100;

              return (
                <motion.div
                  key={sender.name}
                  className="sender-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <div className="sender-rank">{index + 1}</div>
                  <div className="sender-info">
                    <div className="sender-name">{sender.name}</div>
                    <div className="sender-bar-container">
                      <motion.div
                        className="sender-bar"
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ delay: 0.6 + index * 0.1, duration: 0.5 }}
                      />
                    </div>
                  </div>
                  <div className="sender-count">
                    {sender.count.toLocaleString()}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      )}

      {stats.activeYears.length > 0 && (
        <motion.div
          className="active-years"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <h3>
            <Calendar size={18} />
            <span>Active Years</span>
          </h3>
          <div className="years-list">
            {stats.activeYears.map((year) => (
              <div key={year} className="year-tag">
                {year}
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};
